import {
  postJsMessage,
  registerSpecificNotificationHandler,
  unregisterSpecificNotificationHandler,
} from '../../bridge';
import API from '../../api';
import { PostMessageOption } from '../../types';
import { isAvailable, ERROR_CODE, genErrorMessage } from '../utils';
import { Platform } from '../../utils/platform';
import { openSysDlgSelectPath, UnifyWebviewPathIntention } from '../filesystem';

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * download.startDownload
 *
 * [PLATFORM]
 * mac        6.0.5+
 * windows    6.0.5+
 * linux      6.0.5+
 * ios        6.3.0+
 * android    6.3.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export function startDownload(
  params: startDownloadRequest,
  options?: PostMessageOption
) {
  return isAvailable('download.startDownload').then(() => {
    const { url, useDefaultDownloadPath } = params;
    if (
      !useDefaultDownloadPath &&
      (Platform.isMacOS() || Platform.isLinux() || Platform.isWindows())
    ) {
      return openSysDlgSelectPath(
        params.defaultFileName,
        UnifyWebviewPathIntention.Write
      ).then((res) => {
        if (!res.returnCode) {
          const pathId = res.result.pathId;
          // use system default path
          if (pathId) {
            params.pathId = pathId;
            return postJsMessage<startDownloadRequest, startDownloadResponse>(
              API.startDownload,
              { ...params },
              options
            );
          } else {
            return Promise.reject(
              genErrorMessage({
                errorCode: ERROR_CODE.REQUEST_PARAM_ERROR,
                apiName: API.startDownload,
                message: `pathId can't be empty!`,
              })
            );
          }
        } else {
          return Promise.reject(
            genErrorMessage({
              errorCode: ERROR_CODE.CANCEL_SELECT_PATH,
              apiName: API.startDownload,
              message: `pathId is empty!`,
            })
          );
        }
      });
    } else {
      // use system default path, or mobile platform.
      if (url) {
        if (!params.pathId) {
          params.pathId = '';
        }
        return postJsMessage<startDownloadRequest, startDownloadResponse>(
          API.startDownload,
          { ...params },
          options
        );
      } else {
        return Promise.reject(
          genErrorMessage({
            errorCode: ERROR_CODE.REQUEST_PARAM_ERROR,
            apiName: API.startDownload,
            message: `filename can't be empty!`,
          })
        );
      }
    }
  });
}

export type startDownloadRequest = {
  url: string;
  defaultFileName: string;
  needZak: boolean;
  pathId?: string;
  method:
    | 'GET'
    | 'POST'
    | 'PUT'
    | 'DELETE'
    | 'HEAD'
    | 'OPTIONS'
    | 'PATCH'
    | 'TRACE'
    | 'CONNECT';
  headers: {
    [key: string]: string;
  };
  useDefaultDownloadPath: boolean;
};

export type startDownloadResponse = {
  downloadId: string;
};

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * download.pauseDownload
 *
 * [PLATFORM]
 * mac        6.0.5+
 * windows    6.0.5+
 * linux      6.0.5+
 * ios        6.3.0+
 * android    6.3.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export function pauseDownload(downloadId: string, options?: PostMessageOption) {
  return isAvailable('download.pauseDownload').then(() => {
    if (downloadId) {
      return postJsMessage<pauseDownloadRequest, pauseDownloadResponse>(
        API.pauseDownload,
        { downloadId },
        options
      );
    } else {
      return Promise.reject(
        genErrorMessage({
          errorCode: ERROR_CODE.REQUEST_PARAM_ERROR,
          apiName: API.pauseDownload,
          message: `downloadId can't be empty!`,
        })
      );
    }
  });
}

export type pauseDownloadRequest = {
  downloadId: string;
};

export type pauseDownloadResponse = {
  errorCode: number; // 0-success
  errorMessage: string;
};

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * download.resumeDownload
 *
 * [PLATFORM]
 * mac        6.0.5+
 * windows    6.0.5+
 * linux      6.0.5+
 * ios        6.3.0+
 * android    6.3.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export function resumeDownload(
  downloadId: string,
  options?: PostMessageOption
) {
  return isAvailable('download.resumeDownload').then(() => {
    if (downloadId) {
      return postJsMessage<resumeDownloadRequest, resumeDownloadResponse>(
        API.resumeDownload,
        { downloadId },
        options
      );
    } else {
      return Promise.reject(
        genErrorMessage({
          errorCode: ERROR_CODE.REQUEST_PARAM_ERROR,
          apiName: API.resumeDownload,
          message: `downloadId can't be empty!`,
        })
      );
    }
  });
}

export type resumeDownloadRequest = {
  downloadId: string;
};

export type resumeDownloadResponse = {
  errorCode: number; // 0-success
  errorMessage: string;
};

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * download.cancelDownload
 *
 * [PLATFORM]
 * mac        6.0.5+
 * windows    6.0.5+
 * linux      6.0.5+
 * ios        6.3.0+
 * android    6.3.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export function cancelDownload(
  downloadId: string,
  options?: PostMessageOption
) {
  return isAvailable('download.cancelDownload').then(() => {
    if (downloadId) {
      offDownloadProgress(downloadId);
      return postJsMessage<cancelDownloadRequest, cancelDownloadResponse>(
        API.cancelDownload,
        { downloadId },
        options
      );
    } else {
      return Promise.reject(
        genErrorMessage({
          errorCode: ERROR_CODE.REQUEST_PARAM_ERROR,
          apiName: API.cancelDownload,
          message: `downloadId can't be empty!`,
        })
      );
    }
  });
}

export type cancelDownloadRequest = {
  downloadId: string;
};

export type cancelDownloadResponse = {
  errorCode: number; // 0-success
  errorMessage: string;
};

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * download.getDownloadInfo
 *
 * [PLATFORM]
 * mac        6.0.5+
 * windows    6.0.5+
 * linux      6.0.5+
 * ios        6.3.0+
 * android    6.3.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export function getDownloadInfo(
  downloadId: string,
  options?: PostMessageOption
) {
  return isAvailable('download.getDownloadInfo').then(() => {
    if (downloadId) {
      return postJsMessage<getDownloadInfoRequest, getDownloadInfoResponse>(
        API.getDownloadInfo,
        { downloadId },
        options
      );
    } else {
      return Promise.reject(
        genErrorMessage({
          errorCode: ERROR_CODE.REQUEST_PARAM_ERROR,
          apiName: API.getDownloadInfo,
          message: `downloadId can't be empty!`,
        })
      );
    }
  });
}

export type getDownloadInfoRequest = {
  downloadId: string;
};

export type getDownloadInfoResponse = {
  returnCode: number; // 0-success
  errorMsg?: string;
  result: {
    downloadedBytes: number;
    percent: number; // 0-100
    totalSizeInBytes: number;
  };
};

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * download.onDownloadProgress
 *
 * [PLATFORM]
 * mac        6.0.5+
 * windows    6.0.5+
 * linux      6.0.5+
 * ios        6.3.0+
 * android    6.3.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export function onDownloadProgress(
  downloadId: string,
  downloadCallback: (message: downloadCallback) => void
) {
  // 1. register a listening event and monitor the download event through the bridge
  if (Object.keys(downloadProgressList).length === 0) {
    // there is currently no listening event, register one
    registerSpecificNotificationHandler(
      'UnifyNotif_OnDownloadUpdate',
      handleDownloadProgress
    );
  }
  downloadProgressList[downloadId] = function (message: downloadCallback) {
    try {
      downloadCallback(message);
    } catch (e) {
      console.error(
        `[@zoom/hybrid-jssdk] hybrid.download.onDownloadProgress callback failed.`,
        e
      );
    }
  };
}

type downloadCallback = {
  returnCode: number; // 0-success
  errorMsg?: string;
  result: {
    pathId?: string;
    notificationName: string;
    downloadId: string;
    downloadedBytes: number;
    percent: number; // 0-100
    totalSizeInBytes: number;
  };
};

const downloadProgressList: {
  [key: string]: (message: downloadCallback) => void;
} = {};

function handleDownloadProgress(message: downloadCallback) {
  const { returnCode, result } = message;
  const { downloadId, percent } = result;
  if (downloadProgressList[downloadId]) {
    downloadProgressList[downloadId](message);
  }
  if (returnCode !== 0 || percent === 100) {
    // error case or download completed, remove callback
    offDownloadProgress(downloadId);
  }
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * download.offDownloadProgress
 *
 * [PLATFORM]
 * mac        6.0.5+
 * windows    6.0.5+
 * linux      6.0.5+
 * ios        6.3.0+
 * android    6.3.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export function offDownloadProgress(downloadId: string) {
  if (downloadProgressList[downloadId]) {
    delete downloadProgressList[downloadId];
  }
  if (Object.keys(downloadProgressList).length === 0) {
    // All finished, remove the listening event
    unregisterSpecificNotificationHandler(
      'UnifyNotif_OnDownloadUpdate',
      handleDownloadProgress
    );
  }
}

export default {
  startDownload,
  pauseDownload,
  resumeDownload,
  cancelDownload,
  getDownloadInfo,
  onDownloadProgress,
  offDownloadProgress,
};
